import { useEffect, useState } from 'react';
import { getMyAccess, type MyAccess } from '../api/access';

interface UseMyAccessResult {
  myAccess: MyAccess | null;
  /** True until the fetch settles (success or failure) — lets a caller tell
   *  "still waiting" apart from "failed, myAccess stays null". */
  loading: boolean;
}

// Shared across every caller so the access context is fetched once per page
// load, not once per mount. A role change mid-session shows up after a reload.
let cachedAccess: MyAccess | null = null;
let inFlight: Promise<MyAccess> | null = null;

const loadAccess = (): Promise<MyAccess> => {
  if (!inFlight) {
    inFlight = getMyAccess()
      .then(access => {
        cachedAccess = access;
        return access;
      })
      .catch(error => {
        inFlight = null; // let a later mount retry
        throw error;
      });
  }
  return inFlight;
};

/** Clears the shared cache. For tests. */
export const resetMyAccessCache = (): void => {
  cachedAccess = null;
  inFlight = null;
};

/** Returns the caller's global access, fetched once and shared across callers. */
export const useMyAccess = (): UseMyAccessResult => {
  const [myAccess, setMyAccess] = useState<MyAccess | null>(cachedAccess);
  const [loading, setLoading] = useState(cachedAccess === null);

  useEffect(() => {
    if (cachedAccess) return;
    let cancelled = false;
    loadAccess()
      .then(access => { if (!cancelled) setMyAccess(access); })
      .catch(error => console.error('Failed to load access context:', error))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { myAccess, loading };
};
